import React, { useMemo, useState } from 'react';
import { Building2, ChevronRight, Loader2, Plus, Search } from 'lucide-react';
import { useAsyncData } from '../features/directory/useAsyncData';
import { cardClass, formatRelativeDate, inputClass, pageTitle, primaryButton } from './ui/styles';
import { plural, t } from '../lib/i18n';

interface CompaniesViewProps {
  isDarkMode: boolean;
  revision: number;
  onOpenCompany: (companyId: string) => void;
  onChanged: () => void;
  onNotify: (kind: 'success' | 'error' | 'info', text: string) => void;
}

/**
 * Şirketler — toplantıların kurum bazında gruplandığı müşteri arşivi.
 * Takvim katılımcılarının e-posta domain'inden otomatik oluşur
 * (ayse@acme.com → "Acme"); elle de eklenebilir.
 */
export const CompaniesView: React.FC<CompaniesViewProps> = ({
  isDarkMode,
  revision,
  onOpenCompany,
  onChanged,
  onNotify,
}) => {
  const [localRevision, setLocalRevision] = useState(0);
  const companies = useAsyncData(() => window.zabit.listCompanies(), `${revision}:${localRevision}`);
  const [query, setQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [draft, setDraft] = useState({ name: '', domain: '' });
  const [isSaving, setIsSaving] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('tr');
    const list = companies.data ?? [];
    return q ? list.filter((c) => [c.name, c.domain].some((v) => v?.toLocaleLowerCase('tr').includes(q))) : list;
  }, [companies.data, query]);

  const create = async () => {
    setIsSaving(true);
    try {
      const company = await window.zabit.createCompany(draft.name.trim(), draft.domain.trim() || undefined);
      setDraft({ name: '', domain: '' });
      setIsCreating(false);
      setLocalRevision((v) => v + 1);
      onChanged();
      onOpenCompany(company.id);
    } catch (err) {
      onNotify('error', (err as Error).message || t('Şirket oluşturulamadı.', 'Could not create the company.'));
    } finally {
      setIsSaving(false);
    }
  };

  const field = `px-3 py-2 rounded-xl border text-[13px] outline-none transition-colors ${inputClass(isDarkMode)}`;

  return (
    <div className="max-w-3xl mx-auto w-full px-6 py-8 space-y-6 select-none">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className={pageTitle(isDarkMode)}>{t('Şirketler', 'Companies')}</h1>
          <p className="text-[13px] text-neutral-500 mt-1.5">
            {t(
              'Toplantılarının kurum bazlı arşivi: bir şirketin kişileri, kayıtları, özetleri ve kararları tek yerde.',
              'Your meetings archived by company: its people, recordings, summaries and decisions in one place.'
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border w-full sm:w-52 ${inputClass(isDarkMode)}`}>
            <Search className="w-4 h-4 text-neutral-500 shrink-0" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('Şirket ara…', 'Search companies…')}
              className="bg-transparent outline-none text-[13px] w-full"
            />
          </div>
          <button onClick={() => setIsCreating((v) => !v)} className={primaryButton}>
            <Plus className="w-3.5 h-3.5" /> {t('Yeni', 'New')}
          </button>
        </div>
      </div>

      {isCreating && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.name.trim()) void create();
          }}
          className={`p-4 rounded-2xl border flex flex-col sm:flex-row gap-2 ${cardClass(isDarkMode)}`}
        >
          <input
            autoFocus
            className={`${field} flex-1`}
            placeholder={t('Şirket adı', 'Company name')}
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
          <input
            className={`${field} sm:w-48`}
            placeholder={t('domain (ör. acme.com)', 'domain (e.g. acme.com)')}
            value={draft.domain}
            onChange={(e) => setDraft({ ...draft, domain: e.target.value })}
          />
          <button type="submit" disabled={isSaving || !draft.name.trim()} className={primaryButton}>
            {isSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {t('Oluştur', 'Create')}
          </button>
        </form>
      )}

      {companies.isLoading && (
        <p className="text-[13px] text-neutral-500 flex items-center gap-2 py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> {t('Yükleniyor…', 'Loading…')}
        </p>
      )}
      {companies.error && <p className="text-[13px] text-rose-400">{companies.error}</p>}

      {!companies.isLoading && !companies.error && filtered.length === 0 && (
        <div className="text-center py-12 space-y-2">
          <Building2 className="w-8 h-8 text-neutral-600 mx-auto" />
          <p className="text-[13px] text-neutral-500">
            {query
              ? t('Aramaya uyan şirket yok.', 'No companies match your search.')
              : t(
                  'Henüz şirket yok. Kurumsal e-postalı katılımcıların olduğu toplantılar kaydedildikçe şirketler kendiliğinden oluşur.',
                  'No companies yet. They’re created automatically as you record meetings with participants who have work email addresses.'
                )}
          </p>
        </div>
      )}

      {filtered.length > 0 && (
        <div className={`rounded-2xl border divide-y ${cardClass(isDarkMode)} ${isDarkMode ? 'divide-[#222228]' : 'divide-neutral-100'}`}>
          {filtered.map((company) => (
            <button
              key={company.id}
              onClick={() => onOpenCompany(company.id)}
              className="w-full flex items-center justify-between px-4 py-3.5 gap-3 group text-left hover:bg-white/[0.02]"
            >
              <div className="flex items-center gap-3.5 min-w-0">
                <div className="w-9 h-9 rounded-xl bg-neutral-800 border border-neutral-700/50 text-[#46CBE2] font-bold text-[13px] flex items-center justify-center shrink-0">
                  {company.name.charAt(0).toLocaleUpperCase('tr')}
                </div>
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold text-neutral-200 truncate group-hover:text-[#46CBE2] transition-colors">
                    {company.name}
                  </div>
                  <div className="text-[12px] text-neutral-500 truncate">
                    {[company.domain, t(`${company.peopleCount} kişi`, plural(company.peopleCount, 'person', 'people'))]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right">
                  <div className="text-[13px] font-mono text-neutral-400">
                    {t(`${company.meetingCount} toplantı`, plural(company.meetingCount, 'meeting', 'meetings'))}
                  </div>
                  <div className="text-[11px] text-neutral-600">
                    {t('son', 'last')}: {formatRelativeDate(company.lastMeetingISO)}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-neutral-600 group-hover:text-[#46CBE2]" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
